
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useSEO } from '../../hooks/useSEO';
import { generateVideoListSchema, generateBreadcrumbSchema } from '../../utils/seo';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';
import { getYouTubeThumbnail, getYouTubeEmbedUrl, getInstagramEmbedUrl } from '../../lib/youtube';

export default function VideosPage() {
  const [videos, setVideos] = useState<any[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    loadVideos();
  }, []);

  async function loadVideos() {
    const { data } = await supabase
      .from('videos')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setVideos(data);
      if (data.length > 0) setSelectedVideo(data[0]);
    }
    setLoading(false);
  }

  function getVideoUrl(video: any): string {
    if (video.tipo === 'instagram') return video.instagram_url || video.youtube_url;
    return video.youtube_url;
  }

  function getThumbnailUrl(video: any): string {
    if (video.tipo === 'instagram') {
      return 'https://public.readdy.ai/ai/img_res/ceb6d3c2684498f8573b1825689db1a3.jpg';
    }
    return getYouTubeThumbnail(video.youtube_url);
  }

  // Função de compartilhamento
  const shareVideo = (platform: string) => {
    if (!selectedVideo) return;

    const text = `Confira este vídeo: ${selectedVideo.titulo}`;
    const videoUrl = getVideoUrl(selectedVideo);

    let shareUrl = '';

    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${text}\n${videoUrl}`)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(videoUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(videoUrl)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(videoUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(videoUrl);
        setShowShareMenu(false);
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
      setShowShareMenu(false);
    }
  };

  // SEO
  useSEO({
    title: 'Vídeos de Imóveis em Aracaju e Barra dos Coqueiros | Mateus Oliveira',
    description: 'Assista aos vídeos dos nossos imóveis em Aracaju e Barra dos Coqueiros. Tours virtuais, dicas de mercado imobiliário e muito mais. WhatsApp (79) 99988-1002',
    keywords: 'vídeos imóveis aracaju, tour virtual imóveis sergipe, vídeos casas aracaju, apartamentos vídeo sergipe, mateus oliveira vídeos',
    canonical: window.location.origin + '/videos',
    schema: videos.length > 0 ? [
      generateVideoListSchema(videos),
      generateBreadcrumbSchema([{ name: 'Vídeos', url: '/videos' }]),
    ] : generateBreadcrumbSchema([{ name: 'Vídeos', url: '/videos' }]),
  });

  // Renderizar player baseado no tipo
  function renderPlayer(video: any) {
    if (video.tipo === 'instagram') {
      return (
        <div className="rounded-2xl overflow-hidden shadow-xl mb-6 bg-white" style={{ height: '500px' }}>
          <iframe
            src={getInstagramEmbedUrl(video.instagram_url || video.youtube_url)}
            className="w-full h-full border-0"
            allowFullScreen
            title={video.titulo}
          ></iframe>
        </div>
      );
    }
    return (
      <div className="rounded-2xl overflow-hidden shadow-xl mb-6" style={{ height: '500px' }}>
        <iframe
          src={getYouTubeEmbedUrl(video.youtube_url)}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={video.titulo}
        ></iframe>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={[{ name: 'Vídeos', url: '/videos' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Vídeos dos <span className="text-orange-500">Imóveis</span>
            </h1>
            <p className="text-xl text-gray-600">
              Conheça nossos imóveis através de tours virtuais e vídeos exclusivos
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-video-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-500">Nenhum vídeo disponível no momento</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Player Principal */}
              <div className="lg:col-span-2">
                {selectedVideo && (
                  <div>
                    {renderPlayer(selectedVideo)}
                    <div className="bg-white rounded-2xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {selectedVideo.titulo}
                          </h2>
                          <div className="flex items-center gap-2">
                            {selectedVideo.destaque && (
                              <span className="inline-block bg-orange-100 text-orange-800 px-3 py-1 rounded-lg text-sm font-medium">
                                Em Destaque
                              </span>
                            )}
                            <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium ${
                              selectedVideo.tipo === 'instagram'
                                ? 'bg-pink-100 text-pink-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              <i className={`${selectedVideo.tipo === 'instagram' ? 'ri-instagram-line' : 'ri-youtube-line'} mr-1`}></i>
                              {selectedVideo.tipo === 'instagram' ? 'Instagram Reel' : 'YouTube'}
                            </span>
                          </div>
                        </div>
                        <div className="relative">
                          <button
                            onClick={() => setShowShareMenu(!showShareMenu)}
                            className="p-3 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors cursor-pointer"
                            title="Compartilhar"
                          >
                            <i className="ri-share-line text-xl"></i>
                          </button>
                          
                          {showShareMenu && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50">
                              <button
                                onClick={() => shareVideo('whatsapp')}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <i className="ri-whatsapp-line text-xl text-green-600"></i>
                                <span className="font-medium text-gray-700">WhatsApp</span>
                              </button>
                              <button
                                onClick={() => shareVideo('facebook')}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <i className="ri-facebook-circle-line text-xl text-blue-600"></i>
                                <span className="font-medium text-gray-700">Facebook</span>
                              </button>
                              <button
                                onClick={() => shareVideo('twitter')}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <i className="ri-twitter-x-line text-xl text-gray-900"></i>
                                <span className="font-medium text-gray-700">Twitter</span>
                              </button>
                              <button
                                onClick={() => shareVideo('linkedin')}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <i className="ri-linkedin-box-line text-xl text-blue-700"></i>
                                <span className="font-medium text-gray-700">LinkedIn</span>
                              </button>
                              <div className="border-t border-gray-200 my-2"></div>
                              <button
                                onClick={() => shareVideo('copy')}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <i className="ri-file-copy-line text-xl text-gray-600"></i>
                                <span className="font-medium text-gray-700">Copiar Link</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Lista de Vídeos */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Todos os Vídeos</h3>
                  <div className="space-y-4">
                    {videos.map((video) => (
                      <button
                        key={video.id}
                        onClick={() => setSelectedVideo(video)}
                        className={`w-full text-left rounded-lg overflow-hidden transition-all cursor-pointer ${
                          selectedVideo?.id === video.id
                            ? 'ring-4 ring-orange-500'
                            : 'hover:ring-2 hover:ring-gray-300'
                        }`}
                      >
                        <div className="relative h-32 w-full">
                          <img
                            src={getThumbnailUrl(video)}
                            alt={video.titulo}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                              <i className="ri-play-fill text-2xl text-gray-900"></i>
                            </div>
                          </div>
                          {/* Badge de tipo */}
                          <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold text-white ${
                            video.tipo === 'instagram'
                              ? 'bg-gradient-to-r from-pink-600 to-orange-500'
                              : 'bg-red-600'
                          }`}>
                            <i className={video.tipo === 'instagram' ? 'ri-instagram-line' : 'ri-youtube-line'}></i>
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="font-medium text-gray-900 text-sm line-clamp-2">
                            {video.titulo}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
